import type { OnGroupDataMessageArgs } from "@azure/web-pubsub-client";
import {
  ArrowBack as ArrowBackIcon,
  ContentCopyOutlined,
  FileDownloadOutlined,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { toast } from "react-toastify";
import DevelopmentProgress, {
  type AppProgressInfo,
  type DevelopingJob,
  type DevelopmentStep,
  type DocumentProgressInfo,
} from "~/components/AppGeneration/DevelopmentProgress";
import DocumentCard from "~/components/AppGeneration/DocumentCard";
import { TOAST_MESSAGES } from "~/constants/messages";
import { useWebPubSub } from "~/features/projectTop/webPubSubContext";
import { DocumentApi } from "~/functions/api/document";
import type {
  KintoneSystem,
  KintoneSystemLogs,
  ProcessName,
  ProcessStatus,
} from "~/functions/api/kintoneSystem";
import {
  getCurrentStepFromLogs,
  getOverallStatusFromLogs,
  KintoneSystemApi,
} from "~/functions/api/kintoneSystem";
import DocumentIcon from "~/images/document.svg?react";
import TableIcon from "~/images/table.svg?react";
import type { RootState } from "~/store";
import {
  buildAppGenerationOverallNotification,
  type NotifiedOverallStatus,
  shouldNotifyOverallStatus,
} from "~/utils/appGenerationNotificationUtils";
import { toDisplayString } from "~/utils/customDate";
import {
  downloadFile,
  fetchCustomizeCode,
  isSasUrlExpired,
  parseCustomizeCodeUrl,
} from "~/utils/customizeCodeUrl";
import { buildKintoneSpaceUrl } from "~/utils/kintoneUrl";
import { useNativePushNotification } from "~/utils/useNativePushNotification";
import { generateJobStatusGroupName, joinGroup, leaveGroup } from "~/utils/webPubSubUtils";
import styles from "./index.module.scss";

// コンテンツがマークダウン形式かどうかを判定
const isMarkdownContent = (content: string): boolean => {
  try {
    const parsed = JSON.parse(content);
    // オブジェクトまたは配列の場合はJSON形式
    if (typeof parsed === "object" && parsed !== null) {
      return false;
    }
    // 文字列の場合（JSONエスケープされたMarkdown）はマークダウン
    return typeof parsed === "string";
  } catch {
    // JSONパースに失敗した場合はマークダウンとして扱う
    return true;
  }
};

const AppGenerationDetail: React.FC = () => {
  const navigate = useNavigate();
  const { spaceId } = useParams<{ spaceId: string }>();
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const { client } = useWebPubSub();
  const { sendNotificationWithNavigation } = useNativePushNotification();
  const logsRef = useRef<KintoneSystemLogs | null>(null);
  const lastNotifiedOverallStatusRef = useRef<NotifiedOverallStatus | null>(null);

  const [system, setSystem] = useState<KintoneSystem | null>(null);
  const [documents, setDocuments] = useState<{
    appDesign: { id: string; content?: string; modifiedAt?: string } | null;
    fieldDesign: { id: string; content?: string; modifiedAt?: string } | null;
    customizeSpec: { id: string; content?: string; modifiedAt?: string } | null;
  }>({
    appDesign: null,
    fieldDesign: null,
    customizeSpec: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<DevelopmentStep>("app-creation");

  // SAS URLから取得したカスタマイズコードの状態
  const [customizeCodesFromSas, setCustomizeCodesFromSas] = useState<
    Array<{
      fileName: string;
      appId: string;
      content: string;
      isLoading: boolean;
      error?: string;
      url: string;
    }>
  >([]);
  const [isLoadingCustomizeCodes, setIsLoadingCustomizeCodes] = useState(false);

  const kintoneSystemApi = useRef(new KintoneSystemApi());

  // ドキュメントからアプリ情報を抽出
  // outputs.kintone_apps があればそれを優先、なければ既存のdocuments.fieldDesignからの抽出にフォールバック
  const apps = useMemo((): AppProgressInfo[] => {
    // 1. outputs.kintone_apps がある場合はそれを使用（アプリ名+説明付き）
    if (system?.outputs?.kintone_apps && system.outputs.kintone_apps.length > 0) {
      return system.outputs.kintone_apps.map(
        (app): AppProgressInfo => ({
          app_id: app.app_id,
          app_name: app.app_name || `アプリ ${app.app_id}`,
          description: app.description || "",
          fields: [],
          hasCustomize: false,
        }),
      );
    }

    // 2. フォールバック: documents.fieldDesign から抽出
    if (!documents.fieldDesign?.content) return [];

    try {
      const parsed = JSON.parse(documents.fieldDesign.content);
      // マークダウン形式の場合は空配列
      if (typeof parsed === "string") return [];

      const appsArray = Array.isArray(parsed) ? parsed : parsed.apps || [];

      // カスタマイズ対象アプリを取得
      const customizeApps: string[] = [];
      const customizeCounts: Record<string, number> = {};
      if (documents.customizeSpec?.content) {
        try {
          const customizeParsed = JSON.parse(documents.customizeSpec.content);
          if (
            typeof customizeParsed === "object" &&
            customizeParsed !== null &&
            customizeParsed.rows
          ) {
            customizeParsed.rows.forEach((row: string[]) => {
              const appName = row[1];
              if (appName) {
                customizeApps.push(appName);
                customizeCounts[appName] = (customizeCounts[appName] || 0) + 1;
              }
            });
          }
        } catch {
          // ignore parse error
        }
      }

      return (
        appsArray as Array<{
          app_id: string;
          app_name: string;
          properties?: Array<{
            フィールド名: string;
            フィールドコード: string;
            フィールドタイプ: string;
          }>;
        }>
      ).map((app): AppProgressInfo => {
        const hasCustomize = customizeApps.includes(app.app_name);
        return {
          app_id: app.app_id,
          app_name: app.app_name,
          fields: (app.properties || []).map((prop) => ({
            name: prop.フィールド名,
            code: prop.フィールドコード,
            type: prop.フィールドタイプ,
          })),
          hasCustomize,
          ...(hasCustomize ? { customizeCount: customizeCounts[app.app_name] } : {}),
        };
      });
    } catch {
      return [];
    }
  }, [
    system?.outputs?.kintone_apps,
    documents.fieldDesign?.content,
    documents.customizeSpec?.content,
  ]);

  const customizeFiles = useMemo(() => {
    // 1. outputs.kintone_apps から customize_files を取得（概要含む）
    const allCustomizeFiles =
      system?.outputs?.kintone_apps?.flatMap((app) =>
        (app.customize_files ?? []).map((file) => ({
          ...file,
          app_id: app.app_id,
          app_name: app.app_name,
        })),
      ) ?? [];
    if (allCustomizeFiles.length > 0) {
      return allCustomizeFiles.map((file) => {
        const isExpired = isSasUrlExpired(file.url);
        const codeData = customizeCodesFromSas.find((c) => c.url === file.url);
        return {
          name: file.file_name,
          appName: file.app_name || `アプリ ${file.app_id}`,
          summary: file.summary,
          detail: codeData?.content || "",
          url: file.url,
          isExpired,
        };
      });
    }

    // 2. SAS URLからカスタマイズファイル情報を構築
    if (customizeCodesFromSas.length > 0) {
      // カスタマイズ仕様書からファイル名→{アプリ名, 概要}のマップを構築
      const specMap = new Map<string, { appName: string; summary: string }>();
      if (documents.customizeSpec?.content) {
        try {
          const parsed = JSON.parse(documents.customizeSpec.content);
          if (typeof parsed === "object" && parsed !== null && parsed.rows) {
            (parsed.rows as string[][]).forEach((row) => {
              const fileName = row[0] || "";
              if (fileName) {
                specMap.set(fileName, { appName: row[1] || "", summary: row[2] || "" });
              }
            });
          }
        } catch {
          // ignore parse error
        }
      }

      // appId → appName マップ（appsから構築）
      const appIdToName = new Map<string, string>();
      for (const app of apps) {
        if (app.app_id && app.app_name) {
          appIdToName.set(String(app.app_id), app.app_name);
        }
      }

      return customizeCodesFromSas.map((code) => {
        const spec = specMap.get(code.fileName);
        const appName = appIdToName.get(code.appId) || spec?.appName || `アプリ ${code.appId}`;
        const summary = code.isLoading ? "読み込み中..." : code.error || spec?.summary || "";
        const isExpired = isSasUrlExpired(code.url);
        return {
          name: code.fileName,
          appName,
          summary,
          detail: code.content,
          url: code.url,
          isExpired,
        };
      });
    }

    // 3. フォールバック: documents.customizeSpec から抽出
    if (!documents.customizeSpec?.content) return [];
    try {
      const parsed = JSON.parse(documents.customizeSpec.content);
      if (typeof parsed === "string" || !parsed.rows) return [];
      return parsed.rows.map((row: string[]) => ({
        name: row[0] || "",
        appName: row[1] || "",
        summary: row[2] || "",
        detail: "",
        url: "",
        isExpired: false,
      }));
    } catch {
      return [];
    }
  }, [
    system?.outputs?.kintone_apps,
    customizeCodesFromSas,
    documents.customizeSpec?.content,
    apps,
  ]);

  // DevelopmentProgress用のドキュメント情報
  const progressDocuments = useMemo(
    (): {
      appDesign?: DocumentProgressInfo | null;
      fieldDesign?: DocumentProgressInfo | null;
      customizeSpec?: DocumentProgressInfo | null;
    } => ({
      appDesign: documents.appDesign?.content
        ? {
            content: documents.appDesign.content,
            isMarkdown: isMarkdownContent(documents.appDesign.content),
          }
        : null,
      fieldDesign: documents.fieldDesign?.content
        ? {
            content: documents.fieldDesign.content,
            isMarkdown: isMarkdownContent(documents.fieldDesign.content),
          }
        : null,
      customizeSpec: documents.customizeSpec?.content
        ? {
            content: documents.customizeSpec.content,
            isMarkdown: isMarkdownContent(documents.customizeSpec.content),
          }
        : null,
    }),
    [documents],
  );

  // ジョブが進行中かどうか
  const isJobRunning = system?.status === "queued" || system?.status === "running";

  // データ取得
  const fetchData = useCallback(async () => {
    if (!currentProject?.id || !currentUser?.organization_id || !spaceId) {
      setIsLoading(false);
      return;
    }

    const documentApi = new DocumentApi();

    try {
      // システム情報を取得
      const foundSystem = await kintoneSystemApi.current.getKintoneSystem(
        currentUser.organization_id,
        currentProject.id,
        spaceId,
      );

      if (foundSystem) {
        setSystem(foundSystem);
      }

      // ドキュメント情報を取得
      const appDesignId = currentProject?.outputs?.["app-design"]?.id;
      const fieldDesignId = currentProject?.outputs?.["field-design"]?.id;
      const customizeSpecId = currentProject?.outputs?.["customize-specification"]?.id;

      const docs: typeof documents = {
        appDesign: null,
        fieldDesign: null,
        customizeSpec: null,
      };

      if (appDesignId) {
        try {
          const doc = await documentApi.get(
            currentUser.organization_id,
            currentProject.id,
            appDesignId,
          );
          if (doc) {
            docs.appDesign = {
              id: doc.id,
              content: doc.content,
              modifiedAt: doc.modified_at || doc.created_at,
            };
          }
        } catch (e) {
          console.error("Failed to fetch app design:", e);
        }
      }

      if (fieldDesignId) {
        try {
          const doc = await documentApi.get(
            currentUser.organization_id,
            currentProject.id,
            fieldDesignId,
          );
          if (doc) {
            docs.fieldDesign = {
              id: doc.id,
              content: doc.content,
              modifiedAt: doc.modified_at || doc.created_at,
            };
          }
        } catch (e) {
          console.error("Failed to fetch field design:", e);
        }
      }

      if (customizeSpecId) {
        try {
          const doc = await documentApi.get(
            currentUser.organization_id,
            currentProject.id,
            customizeSpecId,
          );
          if (doc) {
            docs.customizeSpec = {
              id: doc.id,
              content: doc.content,
              modifiedAt: doc.modified_at || doc.created_at,
            };
          }
        } catch (e) {
          console.error("Failed to fetch customize spec:", e);
        }
      }

      setDocuments(docs);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }

    setIsLoading(false);
  }, [currentProject, currentUser, spaceId]);

  // 初期データ取得
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // SAS URLからカスタマイズコードを取得
  useEffect(() => {
    const fetchCustomizeCodes = async () => {
      // kintone_apps から customize_files の URL を取得
      const urls: string[] =
        system?.outputs?.kintone_apps?.flatMap((app) =>
          (app.customize_files ?? []).map((f) => f.url),
        ) || [];

      if (urls.length === 0) {
        setCustomizeCodesFromSas([]);
        return;
      }

      setIsLoadingCustomizeCodes(true);

      // まず、URLからファイル情報を抽出してローディング状態で初期化
      const initialFiles = urls.map((url) => {
        const { fileName, appId } = parseCustomizeCodeUrl(url);
        return {
          fileName,
          appId,
          content: "",
          isLoading: true,
          url,
        };
      });
      setCustomizeCodesFromSas(initialFiles);

      // 各URLから実際のコード内容を取得
      const fetchPromises = urls.map(async (url, index) => {
        try {
          const content = await fetchCustomizeCode(url);
          return { index, content, error: undefined };
        } catch (error) {
          console.error(`Failed to fetch customize code from ${url}:`, error);
          return { index, content: "", error: "コードの取得に失敗しました" };
        }
      });

      const results = await Promise.all(fetchPromises);

      // 結果を反映
      setCustomizeCodesFromSas((prev) =>
        prev.map((file, index) => {
          const result = results[index];
          if (!result) return file;
          const updated = {
            ...file,
            content: result.content,
            isLoading: false,
          };
          if (result.error) {
            return { ...updated, error: result.error };
          }
          return updated;
        }),
      );

      setIsLoadingCustomizeCodes(false);
    };

    fetchCustomizeCodes();
  }, [system?.outputs?.kintone_apps]);

  // WebPubSubでリアルタイム進捗を受信
  // biome-ignore lint/correctness/useExhaustiveDependencies: system.logs は ref 経由で参照しているため依存配列に含めない
  useEffect(() => {
    if (!client || !system?.job_id || !isJobRunning) return;

    const groupName = generateJobStatusGroupName(system.job_id);
    let isSubscribed = true;
    logsRef.current = system.logs;
    lastNotifiedOverallStatusRef.current = null;

    // メッセージハンドラ
    const handleMessage = async (e: OnGroupDataMessageArgs) => {
      if (!isSubscribed) return;

      try {
        const message = JSON.parse(e.message.data as string) as {
          job_id: string;
          process_name: ProcessName;
          status: ProcessStatus;
          at: string;
        };

        // このジョブのメッセージかチェック
        if (message.job_id !== system.job_id) {
          return;
        }

        const nextLogs: KintoneSystemLogs = {
          ...(logsRef.current ?? {}),
          [message.process_name]: { status: message.status },
        };
        logsRef.current = nextLogs;
        const overallStatus = getOverallStatusFromLogs(nextLogs);
        if (
          (overallStatus === "succeeded" || overallStatus === "failed") &&
          shouldNotifyOverallStatus(lastNotifiedOverallStatusRef.current, overallStatus) &&
          currentProject?.id &&
          currentUser?.organization_id &&
          system?.id
        ) {
          lastNotifiedOverallStatusRef.current = overallStatus;
          sendNotificationWithNavigation(
            buildAppGenerationOverallNotification({
              organizationId: currentUser.organization_id,
              projectId: currentProject.id,
              systemId: system.id,
              spaceName: system.space?.name,
              status: overallStatus,
            }),
          );
        }

        // systemを更新（関数型更新で最新のlogsを使用）
        setSystem((prev) => {
          if (!prev) return prev;

          // 最新のlogsを基に更新
          const updatedLogs: KintoneSystemLogs = {
            ...prev.logs,
            [message.process_name]: { status: message.status },
          };

          // 現在のステップを更新
          const step = getCurrentStepFromLogs(updatedLogs);
          setCurrentStep(step);

          // 全体のステータスを計算
          const overallStatus = getOverallStatusFromLogs(updatedLogs);

          // プロセスが失敗した場合
          if (message.status === "failed") {
            toast.error(TOAST_MESSAGES.APP_DEVELOPMENT.GENERATION_FAILED);
            return { ...prev, logs: updatedLogs, status: "failed" };
          }

          // 全プロセス完了時
          if (overallStatus === "succeeded") {
            toast.success(TOAST_MESSAGES.APP_DEVELOPMENT.GENERATION_COMPLETE);
            // サーバーから最新データ（space.idを含む）を再取得
            if (currentUser?.organization_id && currentProject?.id && spaceId) {
              kintoneSystemApi.current
                .getKintoneSystem(currentUser.organization_id, currentProject.id, spaceId)
                .then((updatedSystem) => {
                  if (updatedSystem) {
                    setSystem(updatedSystem);
                  }
                })
                .catch((e) => {
                  console.error("Failed to refresh system data:", e);
                });
            }
            return { ...prev, logs: updatedLogs, status: "succeeded" };
          }

          return { ...prev, logs: updatedLogs };
        });
      } catch (error) {
        console.error("[WebPubSub] Failed to parse message:", error);
      }
    };

    // グループ参加を完了してからハンドラを登録
    const setup = async () => {
      await joinGroup(client, groupName);
      if (isSubscribed) {
        client.on("group-message", handleMessage);
      }
    };

    setup();

    return () => {
      isSubscribed = false;
      leaveGroup(client, groupName);
      client.off("group-message", handleMessage);
    };
  }, [
    client,
    system?.job_id,
    system?.id,
    system?.space?.name,
    isJobRunning,
    currentUser?.organization_id,
    currentProject?.id,
    spaceId,
    sendNotificationWithNavigation,
  ]);

  // キャンセル処理
  const handleCancel = async () => {
    if (!system || !currentProject?.id || !currentUser?.organization_id) return;

    try {
      const success = await kintoneSystemApi.current.cancelKintoneSystem(
        currentUser.organization_id,
        currentProject.id,
        system.id,
      );

      if (success) {
        toast.success(TOAST_MESSAGES.APP_DEVELOPMENT.ABORT_SUCCESS);
        // 一覧ページに戻る
        navigate(`/${currentUser.organization_id}/project/${currentProject.id}/app-generation`);
      } else {
        toast.error(TOAST_MESSAGES.APP_DEVELOPMENT.ABORT_FAILED);
      }
    } catch (error) {
      console.error("Failed to cancel:", error);
      toast.error(TOAST_MESSAGES.APP_DEVELOPMENT.ABORT_FAILED);
    }
  };

  // 戻るボタン
  const handleBack = () => {
    if (!currentProject?.id || !currentUser?.organization_id) {
      navigate(-1);
      return;
    }
    navigate(`/${currentUser.organization_id}/project/${currentProject.id}/app-generation`);
  };

  // kintoneを開く（statusがsucceededのときのみ有効）
  const handleOpenKintone = () => {
    if (system?.status !== "succeeded") return;
    const url = buildKintoneSpaceUrl(system);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // ドキュメントクリック（新しいタブで開く）
  const handleDocumentClick = (type: string, id: string | undefined) => {
    if (!id || !currentProject?.id || !currentUser?.organization_id) return;
    const url = `/${currentUser.organization_id}/project/${currentProject.id}/${type}/${id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // 進捗画面でのドキュメントクリック
  const handleProgressDocumentClick = (documentType: string) => {
    let docId: string | undefined;
    switch (documentType) {
      case "app-design":
        docId = documents.appDesign?.id;
        break;
      case "field-design":
        docId = documents.fieldDesign?.id;
        break;
      case "customize-specification":
        docId = documents.customizeSpec?.id;
        break;
    }
    handleDocumentClick(documentType, docId);
  };

  if (isLoading) {
    return (
      <div className={styles["container"]}>
        <div className={styles["loading"]}>読み込み中...</div>
      </div>
    );
  }

  if (!system) {
    return (
      <div className={styles["container"]}>
        <div className={styles["notFound"]}>スペースが見つかりませんでした</div>
      </div>
    );
  }

  const kintoneUrl = buildKintoneSpaceUrl(system);

  // 進行中のジョブ表示用データ
  const developingJob: DevelopingJob | null = isJobRunning
    ? {
        jobId: system.job_id,
        spaceName: system.space.name,
        subdomain: system.subdomain,
        spaceId: system.space?.id,
        currentStep,
        startedAt: system.created_at,
        createdBy: { id: system.created_by, name: "" },
      }
    : null;

  return (
    <div className={styles["container"]}>
      {/* ページヘッダー */}
      <div className={styles["pageHeader"]}>
        <div className={styles["headerLeft"]}>
          <button type="button" className={styles["backButton"]} onClick={handleBack}>
            <ArrowBackIcon sx={{ width: 24, height: 24 }} />
          </button>
          <h1 className={styles["pageTitle"]}>{system.space.name}</h1>
        </div>
        <div className={styles["headerRight"]}>
          {system?.status === "succeeded" && kintoneUrl && (
            <button
              type="button"
              className={styles["kintoneButton"]}
              onClick={handleOpenKintone}
            >
              kintoneアプリを確認
              <OpenInNewIcon sx={{ width: 16, height: 16, marginLeft: "4px" }} />
            </button>
          )}
        </div>
      </div>

      {/* 進行中の場合は進捗表示 */}
      {developingJob && (
        <DevelopmentProgress
          job={developingJob}
          logs={system?.logs}
          overallStatus={system.status}
          onCancel={handleCancel}
          onDocumentClick={handleProgressDocumentClick}
          apps={apps}
          documents={progressDocuments}
          lastUpdated={system?.created_at}
        />
      )}

      {/* 完了済みの場合はタブUIで表示 */}
      {!isJobRunning && (
        <Tabs className={styles["tabs"]}>
          <TabList className={styles["tabList"]}>
            <Tab className={styles["tab"]} selectedClassName={styles["tabSelected"]}>
              ダッシュボード
            </Tab>
            {system?.status === "succeeded" && (
              <Tab className={styles["tab"]} selectedClassName={styles["tabSelected"]}>
                アプリ
              </Tab>
            )}
            {system?.status === "succeeded" && (
              <Tab className={styles["tab"]} selectedClassName={styles["tabSelected"]}>
                カスタマイズ
              </Tab>
            )}
          </TabList>

          {/* ダッシュボードタブ */}
          <TabPanel className={styles["tabPanel"]}>
            {/* 基本情報 */}
            <section className={styles["section"]}>
              <h2 className={styles["sectionTitle"]}>基本情報</h2>
              <div className={styles["infoCard"]}>
                <div className={styles["infoRow"]}>
                  <span className={styles["infoLabel"]}>スペース</span>
                  <span className={styles["infoValue"]}>{system.space.name}</span>
                </div>
                <div className={styles["infoRow"]}>
                  <span className={styles["infoLabel"]}>サブドメイン</span>
                  <span className={styles["infoValue"]}>{system.subdomain}</span>
                </div>
                <div className={styles["infoRow"]}>
                  <span className={styles["infoLabel"]}>ステータス</span>
                  <span className={styles["infoValue"]}>
                    {system.status === "succeeded"
                      ? "完了"
                      : system.status === "failed"
                        ? "失敗"
                        : system.status === "canceled"
                          ? "キャンセル"
                          : system.status}
                  </span>
                </div>
                <div className={styles["infoRow"]}>
                  <span className={styles["infoLabel"]}>作成日</span>
                  <span className={styles["infoValue"]}>{toDisplayString(system.created_at)}</span>
                </div>
                <div className={styles["infoRow"]}>
                  <span className={styles["infoLabel"]}>作成者</span>
                  <span className={styles["infoValue"]}>{system.created_by}</span>
                </div>
              </div>
            </section>

            {/* ドキュメント */}
            <section className={styles["section"]}>
              <h2 className={styles["sectionTitle"]}>ドキュメント</h2>
              <div className={styles["documentGrid"]}>
                <DocumentCard
                  title="アプリ設計書"
                  icon={<DocumentIcon />}
                  status={documents.appDesign ? "ready" : "not-ready"}
                  modifiedAt={documents.appDesign?.modifiedAt}
                  content={documents.appDesign?.content}
                  documentType="app-design"
                  onClick={() => handleDocumentClick("app-design", documents.appDesign?.id)}
                  showOpenInNewIcon
                />
                <DocumentCard
                  title="フィールド設計書"
                  icon={<TableIcon />}
                  status={documents.fieldDesign ? "ready" : "not-ready"}
                  modifiedAt={documents.fieldDesign?.modifiedAt}
                  content={documents.fieldDesign?.content}
                  documentType="field-design"
                  onClick={() => handleDocumentClick("field-design", documents.fieldDesign?.id)}
                  showOpenInNewIcon
                />
                <DocumentCard
                  title="カスタマイズ仕様書"
                  icon={<DocumentIcon />}
                  status={documents.customizeSpec ? "ready" : "not-ready"}
                  modifiedAt={documents.customizeSpec?.modifiedAt}
                  content={documents.customizeSpec?.content}
                  documentType="customize-specification"
                  onClick={() =>
                    handleDocumentClick("customize-specification", documents.customizeSpec?.id)
                  }
                  showOpenInNewIcon
                />
              </div>
            </section>
          </TabPanel>

          {/* アプリタブ（成功時のみ） */}
          {system?.status === "succeeded" && (
          <TabPanel className={styles["tabPanel"]}>
            <section className={styles["section"]}>
              <h2 className={styles["sectionTitle"]}>kintoneアプリ一覧</h2>
              <div className={styles["appConfigTable"]}>
                <table>
                  <thead>
                    <tr>
                      <th className={styles["colAppId"]}>アプリID</th>
                      <th>アプリ名</th>
                      <th className={styles["colDescription"]}>説明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((app) => (
                      <tr key={app.app_id}>
                        <td>{app.app_id}</td>
                        <td>
                          <a
                            href={`https://${system.subdomain}.cybozu.com/k/${app.app_id}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles["appNameLink"]}
                          >
                            {app.app_name}
                          </a>
                        </td>
                        <td className={styles["descriptionCell"]}>{app.description || "-"}</td>
                      </tr>
                    ))}
                    {apps.length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ textAlign: "center", color: "#888" }}>
                          アプリがありません
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </TabPanel>
          )}

          {/* カスタマイズタブ（成功時のみ） */}
          {system?.status === "succeeded" && (
          <TabPanel className={styles["tabPanel"]}>
            <section className={styles["section"]}>
              <h2 className={styles["sectionTitle"]}>カスタマイズ一覧</h2>
              {isLoadingCustomizeCodes && (
                <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                  カスタマイズコードを読み込み中...
                </div>
              )}
              {!isLoadingCustomizeCodes && (
                <div className={styles["customizeTable"]}>
                  <table>
                    <thead>
                      <tr>
                        <th className={styles["colFileName"]}>ファイル名</th>
                        <th className={styles["colAppName"]}>適用アプリ</th>
                        <th>概要</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customizeFiles.map(
                        (
                          file: {
                            name: string;
                            appName: string;
                            summary: string;
                            detail: string;
                            url: string;
                            isExpired: boolean;
                          },
                          index: number,
                        ) => (
                          <React.Fragment key={`${file.name}-${index}`}>
                            <tr>
                              <td className={styles["fileNameCell"]}>
                                <span>{file.name}</span>
                              </td>
                              <td>{file.appName}</td>
                              <td className={styles["summaryCell"]}>{file.summary}</td>
                            </tr>
                            <tr className={styles["codePreviewRow"]}>
                              <td colSpan={3}>
                                {file.isExpired ? (
                                  <div className={styles["expiredMessage"]}>
                                    表示期限（生成から3ヵ月）を超過したのでこの画面でコードは表示されません。
                                    <br />
                                    適用しているkintoneアプリの設定からご確認ください。
                                  </div>
                                ) : (
                                  <div className={styles["codeSnippet"]}>
                                    <div className={styles["codeSnippetHeader"]}>
                                      <span className={styles["codeSnippetFileName"]}>
                                        {file.name}
                                      </span>
                                      <div className={styles["codeSnippetActions"]}>
                                        <button
                                          type="button"
                                          className={styles["codeSnippetActionButton"]}
                                          onClick={async () => {
                                            if (!file.url) return;
                                            try {
                                              await downloadFile(file.url, file.name);
                                            } catch {
                                              toast.error(
                                                TOAST_MESSAGES.APP_DEVELOPMENT.DOWNLOAD_FAILED,
                                              );
                                            }
                                          }}
                                          title="ダウンロード"
                                          disabled={!file.url}
                                        >
                                          <FileDownloadOutlined sx={{ width: 14, height: 14 }} />
                                        </button>
                                        <button
                                          type="button"
                                          className={styles["codeSnippetActionButton"]}
                                          onClick={async () => {
                                            if (file.detail) {
                                              await navigator.clipboard.writeText(file.detail);
                                              toast.success(
                                                TOAST_MESSAGES.APP_DEVELOPMENT.CODE_COPIED,
                                              );
                                            }
                                          }}
                                          title="コードをコピー"
                                        >
                                          <ContentCopyOutlined sx={{ width: 14, height: 14 }} />
                                        </button>
                                      </div>
                                    </div>
                                    <pre className={styles["codeSnippetBody"]}>
                                      <code>{file.detail || "コードを読み込み中..."}</code>
                                    </pre>
                                  </div>
                                )}
                              </td>
                            </tr>
                          </React.Fragment>
                        ),
                      )}
                      {customizeFiles.length === 0 && (
                        <tr>
                          <td colSpan={3} className={styles["emptyCell"]}>
                            カスタマイズファイルがありません
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </TabPanel>
          )}
        </Tabs>
      )}
    </div>
  );
};

export default AppGenerationDetail;
